import {type SelectOptionItem, Button, Grid, List, Select, useTransition} from '@yandex-delivery/blocks'
import {externalApiURL} from '@yandex-delivery/client-core/api/corp'
import type {Translator} from '@yandex-delivery/core/i18n'
import {useI18n} from '@yandex-delivery/core/i18n'
import type {ServerError} from '@yandex-delivery/core/utils/network'
import {useConfigContext} from '@yandex-delivery/order-form/src/contexts'
import type {AppConfig} from '@yandex-delivery/order-form/src/types'
import {addQuery, get} from '@yandex-delivery/utils'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {Controller, useFormContext} from 'react-hook-form'

import type {LocationDetectedVariant, PickupStation, PickupStationsList} from '../../../api/address/model/types'
import {fetch} from '../../../api/fetch'
import {PointsByCityModal} from './PointsByCityModal/PointsByCityModal'
import type {PickupPointsRequest} from './PointsByCityModal/types'
import {SourcePointDeliveryMethod} from './PointsByCityModal/types'
import * as classes from './PointSelect.classes'
import type {PointSelectProps as Props} from './PointSelect.types'
import {PvzIcon} from './PvzIcon'

export function PointSelect(props: Props): React.ReactElement {
  const {
    name, //
    options = [],
    placeholder,
    trailButtonCaption,
  } = props
  const {control, setValue, watch} = useFormContext()
  const [opened, setOpened] = useState(false)
  const selectedCity = watch(`${name}.city`)
  const {isPending, points} = useFetchDropOffPoints(name, selectedCity?.geo_id)

  const preparedPoints = useMemo<SelectOptionItem<PickupStation>[]>(() => {
    return points.map(point => ({
      id: point.id,
      label: point.name,
      value: point,
    }))
  }, [points])

  const citySelectHandler = useCallback(
    (city?: LocationDetectedVariant) => {
      setValue(`${name}.city`, city)

      /**
       * @BusinessLogic
       * При смене города необходимо сбрасывать поле "Пункт приема"
       */
      setValue(`${name}.point`, undefined, {shouldValidate: false})
    },
    [name, setValue],
  )

  const pointSelectHandler = useCallback(
    (point?: PickupStation) => {
      setValue(`${name}.point`, point)
    },
    [name, setValue],
  )

  return (
    <Controller
      control={control}
      name={name}
      render={({field, fieldState}) => (
        <>
          <Select<PickupStation>
            className={classes.select}
            disabled={options.length === 0}
            error={fieldState.invalid}
            filter={filter}
            helperText={fieldState.error?.message}
            isPending={isPending}
            makeKey={makeKey}
            name={name}
            onChange={(item: SelectOptionItem<PickupStation> | null) => {
              field.onChange(item?.value)
            }}
            options={options}
            placeholder={placeholder}
            portal={false}
            renderOptionText={renderItem}
            trail={
              <Button
                className={classes.button}
                disabled={options.length === 0}
                loading={isPending}
                onPress={() => {
                  setOpened(true)
                }}
                shape="circle"
                size="xs"
                variant="outline"
              >
                {trailButtonCaption}
              </Button>
            }
            value={field.value}
            hideToggleIcon
          />
          {opened && (
            <PointsByCityModal
              isOpen={opened}
              isPending={false}
              onCitySelect={citySelectHandler}
              onClose={() => {
                setOpened(false)
              }}
              onPointSelect={pointSelectHandler}
              points={preparedPoints}
              pointType={SourcePointDeliveryMethod.DropOffPoint}
            />
          )}
        </>
      )}
    />
  )
}

const makeKey: (o: SelectOptionItem<PickupStation>) => string = get('value.id')

function renderItem(item: SelectOptionItem<PickupStation>) {
  return (
    <Grid alignItems="center" size="s" wrap="nowrap" container>
      <Grid item>
        <PvzIcon />
      </Grid>
      <Grid size="s" container item xs>
        <Grid item xs>
          <List.Label variant="body2">
            <span className={classes.label}>{item.label}</span>
          </List.Label>
          <List.Description color="minor" variant="caption1">
            {item.value.name}
          </List.Description>
        </Grid>
      </Grid>
    </Grid>
  )
}

function filter(textValue: string, inputValue: string) {
  return textValue.toLowerCase().includes(inputValue.toLowerCase())
}

function useFetchDropOffPoints(key: string, selectedGeoId?: number) {
  const [{isPending, payload: points = []}, startTransition] = useTransition<PickupStation[], ServerError>(undefined, {
    key: `fetch/points/${key}`,
  })
  const appConfig = useConfigContext()
  const i18n = useI18n()

  useEffect(() => {
    if (selectedGeoId) {
      startTransition(() =>
        fetchPickUpDropOffPoints(
          {
            geo_id: selectedGeoId,
            available_for_c2c_dropoff: true,
            is_not_branded_partner_station: true,
            payment_method: 'already_paid',
          },
          appConfig,
          i18n,
        ),
      )
    }
  }, [selectedGeoId])

  return {
    isPending,
    points,
  }
}
export function fetchPickUpDropOffPoints(
  body: PickupPointsRequest,
  appConfig: AppConfig,
  i18n: Translator,
): Promise<PickupStation[]> {
  return fetch<PickupPointsRequest, PickupStation[], PickupStationsList>(appConfig, {
    url: addQuery(externalApiURL('/platform/pickup-points/list'), {country: appConfig.countryCode}),
    method: 'POST',
    body,
  }).then(({body}: {body: PickupStationsList}) => {
    if (body.points) {
      return body.points
    }

    throw new Error(i18n('ndd.offers.info.error', 'К сожалению, доставка по указанному адресу недоступна'))
  })
}
